import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants';

const Breadcrumb = ({ className = '' }) => {
  const location = useLocation();

  // Route mapping for breadcrumb generation
  const routeMap = {
    [ROUTES.DASHBOARD]: { label: 'Dashboard', parent: null },
    [ROUTES.MARKETERS]: { label: 'Creators', parent: ROUTES.DASHBOARD },
    [ROUTES.USERS]: { label: 'Users', parent: ROUTES.DASHBOARD },
    [ROUTES.ROLES]: { label: 'Roles', parent: ROUTES.DASHBOARD },
    [ROUTES.PROFILE]: { label: 'Profile', parent: ROUTES.DASHBOARD },
    [ROUTES.SETTINGS]: { label: 'Settings', parent: ROUTES.DASHBOARD },
    [ROUTES.ANALYTICS]: { label: 'Analytics', parent: ROUTES.DASHBOARD },
    [ROUTES.CONTENT]: { label: 'Content', parent: ROUTES.DASHBOARD },
  };

  // Generate breadcrumb items from current route
  const generateBreadcrumbs = () => {
    let currentPath = location.pathname;

    // Handle root route
    if (currentPath === '/') {
      currentPath = ROUTES.DASHBOARD;
    }

    const breadcrumbs = [];

    // Handle dynamic routes (with parameters)
    let routeKey = currentPath;
    let currentRoute = routeMap[routeKey];

    // If exact match not found, try to find a pattern match
    if (!currentRoute) {
      const possibleRoutes = Object.keys(routeMap);
      for (const route of possibleRoutes) {
        if (currentPath.startsWith(route) && route !== '/') {
          routeKey = route;
          currentRoute = routeMap[route];
          break;
        }
      }
    }

    // If still no match, create a simple breadcrumb
    if (!currentRoute) {
      const pathSegments = currentPath.split('/').filter(segment => segment);
      if (pathSegments.length > 0) {
        breadcrumbs.push({
          label: 'Dashboard',
          path: ROUTES.DASHBOARD,
          isActive: false,
        });
        breadcrumbs.push({
          label: pathSegments[pathSegments.length - 1]
            .replace(/-/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase()),
          path: currentPath,
          isActive: true,
        });
      }
      return breadcrumbs;
    }

    // Build breadcrumb chain
    const buildChain = route => {
      const routeInfo = routeMap[route];
      if (!routeInfo) return [];

      const chain = [
        {
          label: routeInfo.label,
          path: route,
          isActive: route === routeKey,
        },
      ];

      if (routeInfo.parent) {
        return [...buildChain(routeInfo.parent), ...chain];
      }

      return chain;
    };

    return buildChain(routeKey);
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs if there's only one item (current page)
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className={`flex ${className}`} aria-label='Breadcrumb'>
      <ol className='inline-flex items-center space-x-1 md:space-x-3'>
        {breadcrumbs.map((crumb, index) => (
          <li key={crumb.path} className='inline-flex items-center'>
            {index > 0 && (
              <svg
                className='w-3 h-3 text-gray-400 mx-1'
                aria-hidden='true'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 6 10'
              >
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='m1 9 4-4-4-4'
                />
              </svg>
            )}
            {crumb.isActive ? (
              <span className='ml-1 text-sm font-medium text-gray-500 md:ml-2'>
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className='inline-flex items-center ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2'
              >
                {index === 0 && (
                  <svg
                    className='w-3 h-3 mr-2.5'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path d='m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z' />
                  </svg>
                )}
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
